import { useTranslation } from "react-i18next";
import { PortalPriceModelType, type PriceModelDto } from "../../../autogen/bff-api";

export type PriceModelKey = "proportional" | "linear" | "hybrid";

export const DEFAULT_BREAKPOINT_PERCENT = 50;

export const PRICE_MODEL_OPTIONS = [
  { value: "proportional" as const, type: PortalPriceModelType.Proportional },
  { value: "linear" as const, type: PortalPriceModelType.Linear },
  { value: "hybrid" as const, type: PortalPriceModelType.Hybrid },
] as const;

export const priceModelKey = (priceModel?: PriceModelDto | null): PriceModelKey =>
  priceModel?.hybrid ? "hybrid" : priceModel?.linear ? "linear" : "proportional";

export const priceModelType = (priceModel?: PriceModelDto | null): PortalPriceModelType =>
  PRICE_MODEL_OPTIONS.find((o) => o.value === priceModelKey(priceModel))?.type ?? PortalPriceModelType.Proportional;

export const usePriceModelDescription = () => {
  const { t } = useTranslation();
  return (type: PortalPriceModelType, breakpointPercent?: number | null) => {
    switch (type) {
      case PortalPriceModelType.Linear:
        return t(
          "The lowest price gets the full 10 points, and points are deducted in step with the percentage above the lowest price - a price 50 % above the lowest gets 5 points. Prices more than twice the lowest get negative points.",
        );
      case PortalPriceModelType.Hybrid:
        return t(
          "Points fall linearly from 10 at the lowest price down to the breakpoint at {{breakpointPercent}} % above the lowest price, and proportionally after it so the points stay above zero.",
          { breakpointPercent },
        );
      default:
        return t("The lowest price gets the full 10 points, and every other price gets 10 × (lowest ÷ that price).");
    }
  };
};
